import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center" }
const _hoisted_2 = { class: "inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-3xl sm:w-full flex flex-col" }
const _hoisted_3 = { class: "bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto flex-1" }
const _hoisted_4 = { class: "space-y-5" }
const _hoisted_5 = {
  key: 0,
  class: "absolute z-20 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-48 overflow-y-auto"
}
const _hoisted_6 = ["value"]
const _hoisted_7 = {
  key: 0,
  class: "px-3 py-2 text-sm text-slate-400 italic"
}
const _hoisted_8 = {
  key: 0,
  class: "flex items-center gap-2 mt-2 text-sm text-slate-400"
}
const _hoisted_9 = {
  key: 1,
  class: "flex flex-wrap gap-1.5 mt-2"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = {
  key: 2,
  class: "mt-1 text-xs text-slate-400 italic"
}
const _hoisted_12 = {
  key: 0,
  class: "absolute z-20 mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg max-h-48 overflow-y-auto"
}
const _hoisted_13 = ["value"]
const _hoisted_14 = {
  key: 0,
  class: "px-3 py-2 text-sm text-slate-400 italic"
}
const _hoisted_15 = {
  key: 0,
  class: "flex items-center gap-2 mt-2 text-sm text-slate-400"
}
const _hoisted_16 = {
  key: 1,
  class: "flex flex-wrap gap-1.5 mt-2"
}
const _hoisted_17 = ["onClick"]
const _hoisted_18 = {
  key: 2,
  class: "mt-1 text-xs text-slate-400 italic"
}
const _hoisted_19 = { class: "bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0" }
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { key: 1 }
const _hoisted_22 = ["disabled"]

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { positionService, type Position } from '@/services/positionService'
import { employeeService, type Employee } from '@/services/employeeService'
import { Loader2, X } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'ViewersEditModal',
  props: {
    isOpen: { type: Boolean },
    materialId: {},
    positionViewerIds: {},
    userViewerIds: {},
    loading: { type: Boolean }
  },
  emits: ["close", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const positions = ref<Position[]>([])
const employees = ref<Employee[]>([])
const selectedPositionIds = ref<number[]>([])
const selectedEmployeeIds = ref<number[]>([])
const fetching = ref(false)

const positionSearch = ref('')
const employeeSearch = ref('')

const positionOpen = ref(false)
const employeeOpen = ref(false)
const positionContainer = ref<HTMLElement | null>(null)
const employeeContainer = ref<HTMLElement | null>(null)

const filteredPositions = computed(() => {
  if (!positionSearch.value) return positions.value
  const q = positionSearch.value.toLowerCase()
  return positions.value.filter(p => p.name.toLowerCase().includes(q))
})

const filteredEmployees = computed(() => {
  if (!employeeSearch.value) return employees.value
  const q = employeeSearch.value.toLowerCase()
  return employees.value.filter(e => {
    const name = (e.first_name ? e.first_name + ' ' + e.last_name : e.username || '').toLowerCase()
    const pos = (e.position || '').toLowerCase()
    return name.includes(q) || pos.includes(q)
  })
})

const getPositionName = (id: number) => positions.value.find(p => p.id === id)?.name || 'Unknown'

const getEmployeeName = (id: number) => {
  const emp = employees.value.find(e => e.id === id)
  if (!emp) return 'Unknown'
  const name = emp.first_name ? emp.first_name + ' ' + emp.last_name : emp.username || 'Unknown'
  return emp.position ? `${name} (${emp.position})` : name
}

const removePosition = (id: number) => {
  selectedPositionIds.value = selectedPositionIds.value.filter(v => v !== id)
}

const removeEmployee = (id: number) => {
  selectedEmployeeIds.value = selectedEmployeeIds.value.filter(v => v !== id)
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (positionContainer.value && !positionContainer.value.contains(target)) {
    positionOpen.value = false
  }
  if (employeeContainer.value && !employeeContainer.value.contains(target)) {
    employeeOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
})

let fetched = false
const fetchDependencies = async () => {
  if (fetched) return
  fetching.value = true
  try {
    const [posRes, empRes] = await Promise.all([
      positionService.getPositions(),
      employeeService.getEmployees({ per_page: 1000 })
    ])
    positions.value = posRes
    employees.value = empRes.data || []
    fetched = true
  } catch (err) {
    console.error('Failed to fetch viewers data:', err)
  } finally {
    fetching.value = false
  }
}

watch(() => props.isOpen, async (open) => {
  if (open) {
    await fetchDependencies()
    selectedPositionIds.value = [...props.positionViewerIds]
    selectedEmployeeIds.value = [...props.userViewerIds]
    positionSearch.value = ''
    employeeSearch.value = ''
    positionOpen.value = false
    employeeOpen.value = false
  }
}, { immediate: true })

const handleSave = () => {
  emit('save', {
    materialId: props.materialId,
    positionViewerIds: selectedPositionIds.value,
    userViewerIds: selectedEmployeeIds.value,
  })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "fade" }, {
    default: _withCtx(() => [
      (__props.isOpen)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-50 overflow-y-auto",
            onClick: _cache[8] || (_cache[8] = _withModifiers(($event: any) => (_ctx.$emit('close')), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", {
                class: "fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
              }),
              _createElementVNode("div", _hoisted_2, [
                _cache[13] || (_cache[13] = _createElementVNode("div", { class: "bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6 flex-shrink-0" }, [
                  _createElementVNode("h3", { class: "text-lg leading-6 font-medium text-white" }, "Update Viewers")
                ], -1)),
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", null, [
                      _cache[10] || (_cache[10] = _createElementVNode("label", { class: "text-sm font-medium text-slate-700 dark:text-slate-200" }, " Viewers (By Position) ", -1)),
                      _createElementVNode("div", {
                        class: "relative mt-1",
                        ref_key: "positionContainer",
                        ref: positionContainer
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((positionSearch).value = $event)),
                          type: "text",
                          placeholder: "Search positions...",
                          onFocus: _cache[2] || (_cache[2] = ($event: any) => (positionOpen.value = true)),
                          class: "w-full rounded-xl border border-slate-300 dark:border-slate-600 px-3 py-2 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition"
                        }, null, 544), [
                          [_vModelText, positionSearch.value]
                        ]),
                        (positionOpen.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredPositions.value, (pos) => {
                                return (_openBlock(), _createElementBlock("label", {
                                  key: pos.id,
                                  class: "flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm transition-colors"
                                }, [
                                  _withDirectives(_createElementVNode("input", {
                                    type: "checkbox",
                                    value: pos.id,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedPositionIds).value = $event)),
                                    class: "rounded border-slate-300 text-primary focus:ring-primary"
                                  }, null, 8, _hoisted_6), [
                                    [_vModelCheckbox, selectedPositionIds.value]
                                  ]),
                                  _createElementVNode("span", null, _toDisplayString(pos.name), 1)
                                ]))
                              }), 128)),
                              (!filteredPositions.value.length)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_7, "No positions match"))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true)
                      ], 512),
                      (fetching.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createVNode(_unref(Loader2), { class: "w-4 h-4 animate-spin" }),
                            _cache[9] || (_cache[9] = _createTextVNode(" Loading... ", -1))
                          ]))
                        : (selectedPositionIds.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedPositionIds.value, (id) => {
                                return (_openBlock(), _createElementBlock("span", {
                                  key: 'psel-'+id,
                                  class: "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark"
                                }, [
                                  _createTextVNode(_toDisplayString(getPositionName(id)) + " ", 1),
                                  _createElementVNode("button", {
                                    type: "button",
                                    onClick: ($event: any) => (removePosition(id)),
                                    class: "mr-auto hover:text-primary/70 dark:hover:text-primary-dark/70 leading-none"
                                  }, [
                                    _createVNode(_unref(X), { class: "w-3.5 h-3.5" })
                                  ], 8, _hoisted_10)
                                ]))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("p", _hoisted_11, "None selected"))
                    ]),
                    _createElementVNode("div", null, [
                      _cache[12] || (_cache[12] = _createElementVNode("label", { class: "text-sm font-medium text-slate-700 dark:text-slate-200" }, " Viewers (By Specific Employee) ", -1)),
                      _createElementVNode("div", {
                        class: "relative mt-1",
                        ref_key: "employeeContainer",
                        ref: employeeContainer
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((employeeSearch).value = $event)),
                          type: "text",
                          placeholder: "Search employees...",
                          onFocus: _cache[5] || (_cache[5] = ($event: any) => (employeeOpen.value = true)),
                          class: "w-full rounded-xl border border-slate-300 dark:border-slate-600 px-3 py-2 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition"
                        }, null, 544), [
                          [_vModelText, employeeSearch.value]
                        ]),
                        (employeeOpen.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredEmployees.value, (emp) => {
                                return (_openBlock(), _createElementBlock("label", {
                                  key: emp.id,
                                  class: "flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer text-sm transition-colors"
                                }, [
                                  _withDirectives(_createElementVNode("input", {
                                    type: "checkbox",
                                    value: emp.id,
                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedEmployeeIds).value = $event)),
                                    class: "rounded border-slate-300 text-primary focus:ring-primary"
                                  }, null, 8, _hoisted_13), [
                                    [_vModelCheckbox, selectedEmployeeIds.value]
                                  ]),
                                  _createElementVNode("span", null, _toDisplayString(emp.first_name ? emp.first_name + ' ' + emp.last_name : emp.username) + _toDisplayString(emp.position ? ' (' + emp.position + ')' : ''), 1)
                                ]))
                              }), 128)),
                              (!filteredEmployees.value.length)
                                ? (_openBlock(), _createElementBlock("p", _hoisted_14, "No employees match"))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true)
                      ], 512),
                      (fetching.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _createVNode(_unref(Loader2), { class: "w-4 h-4 animate-spin" }),
                            _cache[11] || (_cache[11] = _createTextVNode(" Loading... ", -1))
                          ]))
                        : (selectedEmployeeIds.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedEmployeeIds.value, (id) => {
                                return (_openBlock(), _createElementBlock("span", {
                                  key: 'esel-'+id,
                                  class: "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark"
                                }, [
                                  _createTextVNode(_toDisplayString(getEmployeeName(id)) + " ", 1),
                                  _createElementVNode("button", {
                                    type: "button",
                                    onClick: ($event: any) => (removeEmployee(id)),
                                    class: "hover:text-primary/70 dark:hover:text-primary-dark/70 leading-none"
                                  }, [
                                    _createVNode(_unref(X), { class: "w-3.5 h-3.5" })
                                  ], 8, _hoisted_17)
                                ]))
                              }), 128))
                            ]))
                          : (_openBlock(), _createElementBlock("p", _hoisted_18, "None selected"))
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "w-full sm:w-auto px-4 py-2 rounded-xl font-semibold text-sm text-white bg-primary hover:bg-secondary shadow-sm hover:shadow-md transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#ff8e48]/60 disabled:opacity-50 sm:ml-3",
                    onClick: handleSave,
                    disabled: __props.loading
                  }, [
                    (__props.loading)
                      ? (_openBlock(), _createBlock(_unref(Loader2), {
                          key: 0,
                          class: "w-4 h-4 mr-2 animate-spin"
                        }))
                      : (_openBlock(), _createElementBlock("span", _hoisted_21, "Update Viewers"))
                  ], 8, _hoisted_20),
                  _createElementVNode("button", {
                    type: "button",
                    class: "w-full sm:w-auto px-4 py-2 rounded-xl font-semibold text-sm bg-slate-50 dark:bg-slate-700/30 text-slate-700 dark:text-slate-200 border border-slate-200/60 dark:border-slate-700/60 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-all disabled:opacity-50",
                    onClick: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('close'))),
                    disabled: __props.loading
                  }, " Cancel ", 8, _hoisted_22)
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})